import { useMemo, useRef } from 'react';
import {
  useComparingConversationsParams,
  useCustomNinjaModelName,
  useLeftPanelActions,
  useLeftPanelState,
  useUserMetadata,
} from 'src/hooks';
import {
  ImageCard,
  LeftPanelSections,
  Message,
  MessageSkill,
  MessageType,
  TaskState,
} from 'src/types';
import { getModelDisplayName } from 'src/utils';
import { ThreadMessageActions } from 'src/components/FlatAppearance/components/ThreadMessageActions';
import styles from './MessageWithTaskProgress.module.scss';
import { DotOutline, Sliders } from '@phosphor-icons/react';
import { SUPER_AGENT_DEEP_RESEARCH_MODEL, SVG_SIZE_M } from 'src/constants';
import { Button } from 'src/v2/commonComponents/Button';
import { useChatHistory } from 'src/hooks';

const MessagesWithSettings = [
  MessageType.RESEARCH_TASK_CREATION_CARD,
  MessageType.CHAT_CARD,
  MessageType.IMAGE_CARD,
];

interface MessageWithTaskProgressProps {
  conversationId?: string;
  message: Message;
  taskId?: string;
  taskState?: TaskState;
  messageSkill: MessageSkill | null;
}

export const MessageWithTaskProgress = ({
  message,
  taskId,
  taskState,
  messageSkill,
}: MessageWithTaskProgressProps) => {
  const { isNinjaRetired } = useChatHistory();
  const containerRef = useRef<HTMLParagraphElement>(null);

  const { payload, message_type, model_ids } = message;

  const { metadata } = useUserMetadata();
  const { setLeftPanelData, toggleLeftPanelExpanded } = useLeftPanelActions();
  const { leftPanelSections, isLeftPanelExpanded } = useLeftPanelState();
  const { customModelName } = useCustomNinjaModelName({
    modelIds: model_ids,
    messageSkill,
  });

  const { isCompareMode } = useComparingConversationsParams();

  const responseAgent = useMemo(() => {
    switch (message_type) {
      case MessageType.RESEARCH_TASK_CREATION_CARD:
      case MessageType.CHAT_CARD:
      case MessageType.IMAGE_CARD:
        return getModelDisplayName({
          targetModel: model_ids?.[0],
          metadata: metadata,
        });
      default:
        return '';
    }
  }, [message_type, model_ids, metadata]);

  const agentTitle = useMemo(
    () => customModelName || responseAgent,
    [customModelName, responseAgent],
  );

  const handleGoToSettingsClick = () => {
    if (
      leftPanelSections === LeftPanelSections.SETTINGS &&
      isLeftPanelExpanded
    ) {
      toggleLeftPanelExpanded();
    } else {
      setLeftPanelData({
        panelType: LeftPanelSections.SETTINGS,
        isExpanded: true,
      });
    }
  };

  const shouldShowSettingsButton = useMemo(
    () =>
      !model_ids?.includes(SUPER_AGENT_DEEP_RESEARCH_MODEL) &&
      !!message_type &&
      MessagesWithSettings.includes(message_type) &&
      !(payload as ImageCard)?.watermarked_images,
    [message_type, payload, model_ids],
  );

  if (!taskId) {
    return null;
  }

  return (
    <div className={styles.rootProgress}>
      <div className={styles.root}>
        <div className={styles.infoRowWrapper} ref={containerRef}>
          {agentTitle && (
            <>
              <span className={styles.modelName}>{agentTitle}</span>
              <DotOutline size={SVG_SIZE_M} weight="fill" />
            </>
          )}
          <span className={styles.skillsLabel}>/{messageSkill}</span>

          {!isNinjaRetired && shouldShowSettingsButton && !isCompareMode && (
            <Button
              color="transparent"
              shape="round"
              onClick={handleGoToSettingsClick}
              className={styles.settingsButton}
            >
              <Sliders size={SVG_SIZE_M} />
            </Button>
          )}
        </div>

        {!isNinjaRetired && (
          <ThreadMessageActions
            taskId={taskId}
            taskState={taskState}
            showStopButton={messageSkill === MessageSkill.SCHEDULER}
          />
        )}
      </div>
    </div>
  );
};
